import { create } from 'zustand';

import type { FeatureFlagNames } from 'api/configuration/features/FeatureFlagConfigurationSetting';
import type { FeatureFlagState } from 'features/configuration/types';
import { filterMap } from 'utils/array';
import { devLog } from 'utils/dev-utils';
import type { Nullable } from 'utils/values';

import { setFeatureFlagsOverridesToSessionStorage } from '../utils/getFeatureFlagsOverrides';

type FeatureFlagsStoreProps = {
  featureFlags: Nullable<FeatureFlagState[]>;
  locked: boolean;
  toggleLock: () => void;
  initialize: (featureFlags: FeatureFlagState[], locked?: boolean) => void;
  disableAll: () => void;
  toggleFeatureFlag: (featureFlagName: FeatureFlagNames) => void;
};

export const useFeatureFlagsStore = create<FeatureFlagsStoreProps>((set, getState) => ({
  featureFlags: [],
  locked: false,
  toggleLock: () => {
    const shouldLock = !getState().locked;
    const featureFlags = getState().featureFlags;

    if (shouldLock) {
      setFeatureFlagsOverridesToSessionStorage(
        filterMap(featureFlags || [], (flag) => (flag.enabled ? flag.name : undefined))
      );
    } else {
      setFeatureFlagsOverridesToSessionStorage(null);
    }

    set({ locked: shouldLock });
  },
  initialize: (featureFlags: FeatureFlagState[], locked = false) => {
    if (locked) {
      setFeatureFlagsOverridesToSessionStorage(
        filterMap(featureFlags, (flag) => (flag.enabled ? flag.name : undefined))
      );
    } else {
      setFeatureFlagsOverridesToSessionStorage(null);
    }
    set({ featureFlags, locked });
  },
  disableAll: () => {
    set(({ featureFlags }) => {
      const updatedFeatureFlags = featureFlags?.map((flag) => ({ ...flag, enabled: false }));
      return { featureFlags: updatedFeatureFlags };
    });
  },
  toggleFeatureFlag: (featureFlagName: FeatureFlagNames) =>
    set(({ featureFlags, locked }) => {
      if (locked) {
        devLog(`Feature flags are locked`);
        return { featureFlags, locked };
      }
      const updatedFeatureFlags = featureFlags?.map((flag) => {
        if (flag.name === featureFlagName) {
          devLog(`Toggled feature flag ${featureFlagName} to ${!flag.enabled}`);
          return { ...flag, enabled: !flag.enabled };
        }
        return flag;
      });

      return { featureFlags: updatedFeatureFlags };
    })
}));
